/**
 * Raster viewports (density, heatmap, ortho color) of a sheet, rendered on
 * the live viewer at the viewport's DPI, one after another: each puts the
 * viewer in its view (floor + view mode), forces its heatmap when it has a
 * source, renders, and puts everything back.
 */

import type { PdfImageResource } from '@pointorama/print-engine';
import { paperMmToWorld, rasterPx, type GradientType, type HeatmapViewportSettings, type ViewportSettings } from 'shared-types';
import { ClipTask } from '../../../potree/defines';
import { framePointsPerM2, renderPrintRaster, type PrintRaster, type PrintRasterFrame, type PrintRasterLoad } from '../../../potree/utils/PrintRasterRenderer';
import { startViewerViewSession, type PrintViewer } from './viewerView';

export const isRasterKind = (kind: ViewportSettings['kind']) => kind !== 'PLAN_VECTOR';

/** Size on paper of a viewport (its element). */
export interface ViewportBox {
  wMm: number;
  hMm: number;
}

export function viewportFrame(box: ViewportBox, settings: Pick<ViewportSettings, 'center' | 'headingDeg' | 'scaleDen'>): PrintRasterFrame {
  return { center: settings.center, headingDeg: settings.headingDeg, widthM: paperMmToWorld(box.wMm, settings.scaleDen), heightM: paperMmToWorld(box.hMm, settings.scaleDen) };
}

export function viewportPixels(box: ViewportBox, dpi: number): { width: number; height: number } {
  return { width: rasterPx(box.wMm, dpi), height: rasterPx(box.hMm, dpi) };
}

/** A plane or surface that can show a deviation heatmap (subset of PointoramaPlane / PointoramaSurface). */
interface HeatmapCapable {
  identifier: string;
  assignedClipTask: number;
  actualClipTask: number;
  gradient?: GradientType;
  tolerance: number;
}

function heatmapCapables(viewer: PrintViewer): HeatmapCapable[] {
  return [...viewer.scene.surfaces, ...viewer.scene.planes];
}

/** The deviation heatmap the viewer shows now (the first plane / surface with its heatmap filter on). */
export function visibleHeatmap(viewer: PrintViewer): HeatmapViewportSettings | null {
  const source = heatmapCapables(viewer).find((s) => s.actualClipTask === ClipTask.HEATMAP);
  if (!source) return null;
  return { attribute: 'deviation', sourceId: source.identifier, gradient: source.gradient ?? 'SPECTRAL', min: -source.tolerance, max: source.tolerance };
}

/**
 * Switch a viewport's heatmap source on (gradient included) for the render.
 * The tolerance is the source's own (a surface's lives in a persisted
 * property, which a print must not change); the range actually printed is
 * returned for the legend.
 */
function forceHeatmap(viewer: PrintViewer, heatmap: HeatmapViewportSettings | null): { range: { min: number; max: number } | null; restore: () => void } {
  const source = heatmap?.sourceId ? heatmapCapables(viewer).find((s) => s.identifier === heatmap.sourceId) : undefined;
  if (!heatmap || !source) return { range: heatmap ? { min: heatmap.min, max: heatmap.max } : null, restore: () => undefined };
  const saved = { assignedClipTask: source.assignedClipTask, gradient: source.gradient };
  source.assignedClipTask = ClipTask.HEATMAP;
  source.gradient = heatmap.gradient;
  return {
    range: { min: -source.tolerance, max: source.tolerance },
    restore: () => {
      source.assignedClipTask = saved.assignedClipTask;
      source.gradient = saved.gradient;
    },
  };
}

/** Meters per screen pixel of the live top-down view. */
export function screenMetersPerPixel(viewer: PrintViewer): number {
  const area = document.getElementById('potree_render_area');
  return (2 * viewer.scene.view.radius) / Math.max(1, area?.clientWidth ?? 1);
}

/** What framing captures for a raster viewport: the view's darkness / heatmap / point footprint as seen now. */
export function captureRasterLook(viewer: PrintViewer, box: ViewportBox, settings: ViewportSettings, frame: Pick<ViewportSettings, 'center' | 'headingDeg' | 'scaleDen'>): Pick<ViewportSettings, 'xray' | 'heatmap' | 'screenMetersPerPixel'> {
  const metersPerPixel = screenMetersPerPixel(viewer);
  return {
    screenMetersPerPixel: metersPerPixel,
    xray:
      settings.kind === 'DENSITY_RASTER'
        ? {
            alpha: viewer.xrayDensityAlpha,
            referenceMetersPerPixel: metersPerPixel,
            referencePointsPerM2: framePointsPerM2(viewer, viewportFrame(box, frame)),
            zMin: settings.xray?.zMin ?? null,
            zMax: settings.xray?.zMax ?? null,
          }
        : settings.xray,
    heatmap: settings.kind === 'HEATMAP_RASTER' ? (visibleHeatmap(viewer) ?? settings.heatmap) : settings.heatmap,
  };
}

export interface RasterJob {
  /** The viewport instance key (`viewportSettingsKey`). */
  key: string;
  box: ViewportBox;
  settings: ViewportSettings;
}

export interface RenderedRaster {
  key: string;
  raster: PrintRaster;
  /** The heatmap range that printed (legend). */
  heatmapRange: { min: number; max: number } | null;
}

export interface RasterRenderProgress {
  index: number;
  total: number;
  stage: 'loading' | 'rendering';
  fraction: number;
}

/** Render one viewport as it is on screen right now (the caller already shows its view). */
export function renderViewportNow(viewer: PrintViewer, job: RasterJob, options: { dpi: number; load: PrintRasterLoad; signal?: AbortSignal; onProgress?: (stage: 'loading' | 'rendering', fraction: number) => void }): Promise<PrintRaster> {
  const { settings } = job;
  const { width, height } = viewportPixels(job.box, options.dpi);
  return renderPrintRaster(viewer, {
    frame: viewportFrame(job.box, settings),
    pixelWidth: width,
    pixelHeight: height,
    mode: settings.kind === 'DENSITY_RASTER' ? 'density' : 'color',
    density:
      settings.kind === 'DENSITY_RASTER'
        ? {
            alpha: settings.xray?.alpha ?? viewer.xrayDensityAlpha,
            referenceMetersPerPixel: settings.xray?.referenceMetersPerPixel ?? paperMmToWorld(job.box.wMm, settings.scaleDen) / width,
            // Only a full-density load is denser than the screen was when the darkness was tuned.
            referencePointsPerM2: options.load === 'full' ? (settings.xray?.referencePointsPerM2 ?? null) : null,
          }
        : undefined,
    screenMetersPerPixel: settings.screenMetersPerPixel,
    load: options.load,
    signal: options.signal,
    onProgress: options.onProgress,
  });
}

/** DPI for an on-screen preview: the print DPI, capped so the long side stays under `longSidePx`. */
export function previewDpi(box: ViewportBox, printDpi: number, longSidePx = 1600): number {
  return Math.min(printDpi, (longSidePx * 25.4) / Math.max(box.wMm, box.hMm));
}

/** Every raster viewport at its print DPI, one at a time. */
export async function renderViewportRasters(viewer: PrintViewer, jobs: readonly RasterJob[], options: { signal?: AbortSignal; onProgress?: (progress: RasterRenderProgress) => void } = {}): Promise<RenderedRaster[]> {
  const out: RenderedRaster[] = [];
  const session = startViewerViewSession(viewer);
  try {
    for (let index = 0; index < jobs.length; index++) {
      const job = jobs[index];
      await session.show({ floorId: job.settings.floorId, kind: job.settings.kind });
      const heatmap = job.settings.kind === 'HEATMAP_RASTER' ? forceHeatmap(viewer, job.settings.heatmap) : null;
      try {
        const raster = await renderViewportNow(viewer, job, {
          dpi: job.settings.dpi,
          load: 'full',
          signal: options.signal,
          onProgress: (stage, fraction) => options.onProgress?.({ index, total: jobs.length, stage, fraction }),
        });
        out.push({ key: job.key, raster, heatmapRange: heatmap?.range ?? null });
      } finally {
        heatmap?.restore();
      }
    }
  } finally {
    await session.end();
  }
  return out;
}

/** Zlib-compressed pixels for a FlateDecode image XObject (pdf-lib compresses them itself when the browser can't). */
export async function rasterPdfImage(raster: PrintRaster): Promise<PdfImageResource> {
  const base = { kind: 'raw' as const, width: raster.width, height: raster.height, channels: raster.channels };
  if (typeof CompressionStream === 'undefined') return { ...base, data: raster.data };
  const stream = new Blob([raster.data.slice().buffer]).stream().pipeThrough(new CompressionStream('deflate'));
  const data = new Uint8Array(await new Response(stream).arrayBuffer());
  return { ...base, data, deflated: true };
}

/** A small PNG of a raster for the on-screen preview (box-filtered down to `maxSide` px). */
export async function rasterPreviewUrl(raster: PrintRaster, maxSide = 1600): Promise<string> {
  const factor = Math.max(1, Math.ceil(Math.max(raster.width, raster.height) / maxSide));
  const width = Math.max(1, Math.floor(raster.width / factor));
  const height = Math.max(1, Math.floor(raster.height / factor));
  const rgba = new Uint8ClampedArray(width * height * 4);
  const c = raster.channels;
  for (let y = 0; y < height; y++) {
    for (let x = 0; x < width; x++) {
      let r = 0;
      let g = 0;
      let b = 0;
      for (let dy = 0; dy < factor; dy++) {
        const row = (y * factor + dy) * raster.width;
        for (let dx = 0; dx < factor; dx++) {
          const s = (row + x * factor + dx) * c;
          r += raster.data[s];
          g += raster.data[s + (c === 3 ? 1 : 0)];
          b += raster.data[s + (c === 3 ? 2 : 0)];
        }
      }
      const n = factor * factor;
      const d = (y * width + x) * 4;
      rgba[d] = r / n;
      rgba[d + 1] = g / n;
      rgba[d + 2] = b / n;
      rgba[d + 3] = 255;
    }
  }
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const context = canvas.getContext('2d');
  if (!context) throw new Error('Canvas 2D context unavailable');
  context.putImageData(new ImageData(rgba, width, height), 0, 0);
  const blob = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, 'image/png'));
  if (!blob) throw new Error('Preview encoding failed');
  return URL.createObjectURL(blob);
}

/** Identity of what a raster shows: a preview is stale once any of this changes. */
export function rasterStamp(settings: ViewportSettings): string {
  const { kind, floorId, center, headingDeg, scaleDen, heatmap, xray, capturedAt } = settings;
  return JSON.stringify({ kind, floorId, center, headingDeg, scaleDen, heatmap, xray, capturedAt });
}
